<script lang="ts">
  import XSSDisplay from '@/Common/XSSDisplay.svelte';
  import DifficultyDiamonds from '@/DifficultyDiamonds.svelte';
  import { formatXSSDetails, kmToMile, convertSeconds } from '@/Util/utils';
  import { type Exercise } from '@/Common/ActivityFunctions';
  import { format_duration } from '@/time';

  export let exercise: Exercise;

  // Derived
  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert?._displayUnits === 'imperial';
  $: distanceVal = exercise?.distance ? (useFreedomUnits ? kmToMile(exercise.distance) : exercise.distance) : 0;
  $: distanceUnit = useFreedomUnits ? 'mi' : 'km';
  $: durationStr = convertSeconds(exercise?.duration ?? exercise?.adu, false);
  $: xssDetails = formatXSSDetails({
    xss: exercise?.xss ?? 0,
    xlss: exercise?.xlss ?? 0,
    xhss: exercise?.xhss ?? 0,
    xpss: exercise?.xpss ?? 0,
  });
  $: difficulty = exercise?.difficulty ?? null;
  $: focusValue = exercise?.focus ?? '';
  $: totalIntervals = exercise?.total_intervals ?? null;
  $: avgPower = typeof exercise?.avg_power === 'number' ? Math.round(exercise.avg_power) : null;
  $: maxPower = typeof exercise?.max_power === 'number' ? Math.round(exercise.max_power) : null;
  $: xep = typeof exercise?.xep === 'number' ? Math.round(exercise.xep) : null;
  $: speedUnit = useFreedomUnits ? 'mph' : 'km/h';
  $: avgSpeed = typeof exercise?.avg_speed === 'number' ? (useFreedomUnits ? (exercise.avg_speed * 0.621371) : exercise.avg_speed) : null;
  $: maxSpeed = typeof exercise?.max_speed === 'number' ? (useFreedomUnits ? (exercise.max_speed * 0.621371) : exercise.max_speed) : null;

</script>

<section class='space-y-0.5'>
  <div class="flex flex-row flex-wrap gap-x-2">
    <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">XSS Breakdown:</h3>
    <div class="flex items-center gap-4 flex-wrap text-sm">
      <XSSDisplay xss={xssDetails} className="text-sm font-semibold whitespace-nowrap" />
    </div>
  </div>
  {#if difficulty}
    <div class="flex flex-row flex-wrap items-center gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Difficulty:</h3>
      <span class='flex flex-row flex-wrap items-center gap-x-1'>
        <DifficultyDiamonds difficulty={difficulty} compact={false} />
        <span class="text-sm font-medium">- {exercise.rating} - {Math.round(difficulty)}</span>
      </span>
    </div>
  {/if}
  {#if distanceVal > 0}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Distance:</h3>
      <span class="text-sm font-medium">{distanceVal.toFixed(1)} {distanceUnit}</span>
    </div>
  {/if}
  {#if durationStr}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Duration:</h3>
      <span class="text-sm font-medium">{durationStr}</span>
    </div>
  {/if}
  {#if focusValue}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Focus:</h3>
      <span class="text-sm font-medium">{focusValue} - {format_duration(exercise.sfd)}</span>
    </div>
  {/if}
  {#if exercise.tgc && exercise.tgf}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Fat/Carbs:</h3>
      <span class="text-sm font-medium">{Math.round(exercise.tgf)} g / {Math.round(exercise.tgc)} g</span>
    </div>
  {/if}
  {#if avgSpeed || maxSpeed}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Speed:</h3>
      <span class="text-sm font-medium">
        {#if avgSpeed && maxSpeed}
          {avgSpeed.toFixed(1)} / {maxSpeed.toFixed(1)} {speedUnit}
        {:else if avgSpeed}
          {avgSpeed.toFixed(1)} {speedUnit}
        {:else}
          {maxSpeed.toFixed(1)} {speedUnit}
        {/if}
      </span>
    </div>
  {/if}
  {#if avgPower || maxPower}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">{avgPower && maxPower ? 'Avg/Max ' : avgPower ? 'Avg ' : maxPower ? 'Max ' : ''}Power:</h3>
      <span class="text-sm font-medium">
        {#if avgPower && maxPower}
          {avgPower} W / {maxPower} W
        {:else if avgPower}
          {avgPower} W
        {:else}
          {maxPower} W
        {/if}
      </span>
    </div>
  {/if}
  {#if xep}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">XEP:</h3>
      <span class="text-sm font-medium">
        {xep} W
      </span>
    </div>
  {/if}
  {#if totalIntervals != null}
    <div class="flex flex-row flex-wrap gap-x-2">
      <h3 class="m-0 whitespace-nowrap text-sm font-medium tracking-wide text-muted-foreground">Intervals:</h3>
      <span class="text-sm font-medium">{totalIntervals}</span>
    </div>
  {/if}
</section>
